// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};
/** @type {HTMLElement | null} */
let trapFocusContainer = null;

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter(
    (element) =>
      element instanceof HTMLElement &&
      element.tabIndex >= 0 &&
      !element.closest('[inert], [aria-hidden="true"]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility === 'visible'
  );
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 * @param {{ focusContainer?: boolean, containFocus?: boolean }} [options] - Non-modal panels may cycle Tab without redirecting outside focus.
 */
export function trapFocus(container, { focusContainer = true, containFocus = true } = {}) {
  // Clean up any previously set traps.
  removeTrapFocus();

  if (!getFocusableElements(container).length) {
    // If nothing is focusable, just abort—no need to trap.
    return;
  }

  trapFocusContainer = container;

  // Keydown handler for cycling focus with Tab and Shift+Tab
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;
    if (!container.isConnected || !container.getClientRects().length) {
      removeTrapFocus(container);
      return;
    }

    // Cart/editor morphs can replace controls while the dialog remains open.
    const focusable = getFocusableElements(container);
    const first = focusable[0];
    // A cart-owned redirect sentinel follows opaque wallet controls. Do not
    // wrap early at the last light-DOM control when a wallet is not enumerable.
    const cartEnd = container.querySelector('[data-cart-focus-end]');
    const last = cartEnd instanceof HTMLElement && cartEnd.tabIndex === 0 && cartEnd.getClientRects().length
      ? cartEnd
      : focusable[focusable.length - 1];
    if (!first || !last) return;

    const activeEl = document.activeElement;

    // If on the last focusable and tabbing forward, go to first
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first?.focus();
    }
    // If on the first (or the container) and shift-tabbing, go to last
    else if (event.shiftKey && (activeEl === first || activeEl === container)) {
      event.preventDefault();
      last?.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    if (!container.isConnected || !container.getClientRects().length) {
      removeTrapFocus(container);
      return;
    }
    // If the newly focused element isn't inside the container, redirect focus back.
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      // E.g., refocus the first focusable element:
      getFocusableElements(container)[0]?.focus();
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  if (containFocus) document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it.
  if (focusContainer) container.focus();
}

/**
 * Remove the focus trap, optionally only when it belongs to this container.
 * @param {HTMLElement} [container] - The owning container or its ancestor.
 */
export function removeTrapFocus(container) {
  if (container && trapFocusContainer && !container.contains(trapFocusContainer)) return;
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  trapFocusContainer = null;
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
